import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  FormHelperText,
  Autocomplete,
  Chip,
} from '@mui/material';
import { useInviteUser } from '../hooks/useUsers';
import { useOrganizations, useSites } from '../hooks/useFleet';
import { useAuth } from '../contexts/AuthContext';
import type { InviteUserRequest } from '../api/types';
import { DraggableDialog } from './DraggableDialog';

const NO_ORG_VALUE = '__no_org__';

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (inviteUrl: string) => void;
  preselectedOrganizationId?: string;
}

const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
  preselectedOrganizationId,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    organization_id: preselectedOrganizationId || '',
    role: 'viewer' as 'admin' | 'editor' | 'viewer',
    message: '',
  });
  const [selectedSites, setSelectedSites] = useState<Array<{ id: string; name: string; role: string }>>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { user: currentUser } = useAuth();
  const isCurrentGlobalAdmin = currentUser?.global_role === 'global_admin';
  const isNoOrg = formData.organization_id === NO_ORG_VALUE;

  const { data: orgsData } = useOrganizations({ size: 100 });
  const { data: sitesData } = useSites({
    organization_id: isNoOrg ? undefined : (formData.organization_id || undefined),
    size: 100,
  });
  const inviteMutation = useInviteUser();

  useEffect(() => {
    if (preselectedOrganizationId) {
      setFormData((prev) => ({ ...prev, organization_id: preselectedOrganizationId }));
    }
  }, [preselectedOrganizationId]);

  useEffect(() => {
    // Reset sites when organization changes
    setSelectedSites([]);
  }, [formData.organization_id]);

  const handleClose = () => {
    setFormData({
      email: '',
      name: '',
      organization_id: preselectedOrganizationId || '',
      role: 'viewer',
      message: '',
    });
    setSelectedSites([]);
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitError('Please enter a valid email address');
      return;
    }

    if (!formData.organization_id) {
      setSubmitError('Please select an organization');
      return;
    }

    try {
      const request: InviteUserRequest = {
        email: formData.email.toLowerCase(),
        name: formData.name || undefined,
        organization_id: isNoOrg ? undefined : formData.organization_id,
        role: formData.role,
        message: formData.message || undefined,
        resource_assignments: isNoOrg
          ? []
          : selectedSites.map((site) => ({
              resource_type: 'site',
              resource_id: site.id,
              role: site.role,
            })),
      };

      const result = await inviteMutation.mutateAsync(request);

      if (result.invite_url) {
        onSuccess(result.invite_url);
      } else if (result.added_to_org) {
        // User already existed and was added to org
        handleClose();
      }
    } catch (err: any) {
      console.error('Invite error:', err);
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to invite user');
    }
  };

  const organizations = orgsData?.data || [];
  const sites = sitesData?.data || [];

  const actionsContent = (
    <>
      <Button onClick={handleClose}>Cancel</Button>
      <Button
        onClick={handleSubmit}
        variant="contained"
        disabled={!formData.email || !formData.organization_id || inviteMutation.isPending}
        sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
      >
        {inviteMutation.isPending ? (
          <CircularProgress size={20} />
        ) : (
          'Send Invite'
        )}
      </Button>
    </>
  );

  return (
    <DraggableDialog
      open={open}
      onClose={handleClose}
      title="Invite User"
      maxWidth="sm"
      actions={actionsContent}
    >
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <TextField
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          fullWidth
          required
          placeholder="user@example.com"
          autoFocus
        />

        <TextField
          label="Name (Optional)"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          fullWidth
          placeholder="John Doe"
          helperText="User can change this when they accept the invite"
        />

        <FormControl fullWidth required>
          <InputLabel>Organization</InputLabel>
          <Select
            value={formData.organization_id}
            label="Organization"
            onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
            SelectDisplayProps={{ 'data-testid': 'invite-org-select' } as any}
          >
            {isCurrentGlobalAdmin && (
              <MenuItem value={NO_ORG_VALUE}>(No organization — for Global Admins)</MenuItem>
            )}
            {organizations.map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {isNoOrg
              ? 'No org membership will be created. User can then be promoted to Global Admin.'
              : 'User will be added to this organization'}
          </FormHelperText>
        </FormControl>

        <FormControl fullWidth required>
          <InputLabel>Role</InputLabel>
          <Select
            value={formData.role}
            label="Role"
            onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
          >
            <MenuItem value="admin">Admin - Full organization access</MenuItem>
            <MenuItem value="editor">Editor - Can manage content and devices</MenuItem>
            <MenuItem value="viewer">Viewer - Read-only access</MenuItem>
          </Select>
        </FormControl>

        {!isNoOrg && formData.organization_id && sites.length > 0 && (
          <Autocomplete
            multiple
            options={sites}
            getOptionLabel={(option) => option.name}
            value={sites.filter((s) => selectedSites.some((ss) => ss.id === s.id))}
            onChange={(_, newValue) => {
              setSelectedSites(newValue.map((site) => ({
                id: site.id,
                name: site.name,
                role: selectedSites.find((s) => s.id === site.id)?.role || 'viewer',
              })));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Site Access (Optional)"
                placeholder="Select specific sites..."
                helperText="Leave empty to give access to all sites in the organization"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option.name}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
          />
        )}

        <TextField
          label="Personal Message (Optional)"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          fullWidth
          multiline
          rows={2}
          placeholder="Welcome to the team! Looking forward to working with you."
          helperText="This message will be shown when the user accepts the invite"
        />
      </Box>
    </DraggableDialog>
  );
};

export default InviteUserDialog;
